import { Form, Input, Spin } from 'antd';
import { SearchIcon } from 'assets/Icons/AppointmentIcons';
import Box from 'components/Box';
import appointmentApis from 'features/appointment/services/apis';
import appointmentSelectors from 'features/appointment/services/selectors';
import { debounce, find } from 'lodash';
import moment, { Moment } from 'moment';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import shopSelectors from 'services/shop/selectors';
import { IStaffItemData, IStaffNextAvailableItem } from 'services/shop/types/staff';
import styled from 'styled-components';
import { selected_box_icon } from '../icons';
import StaffBox from './StaffBox';

export type IStaffListChooseUIRef = {
  setLoading: (body: boolean) => void;
  fetchNextAvailable: () => void;
};

type Props = {
  onPickStaff: (item: IStaffItemData) => void;
  selectedStaffs?: string[];
  staffs: IStaffItemData[];
  hideZoom?: boolean;
};
export const useStaffListChooseUI = () => useRef<IStaffListChooseUIRef>(null);
const StaffListChooseUI = forwardRef<IStaffListChooseUIRef, Props>(({ onPickStaff, selectedStaffs = [], staffs: staffsProps, hideZoom = true }, ref) => {
  const form = Form.useFormInstance();
  const date: Moment | null = Form.useWatch('date', form);
  const [activeTab, setActiveTab] = useState<'view-all' | 'available' | 'next-available'>('view-all');
  const [searchText, setSearchText] = useState('');
  const _allStaff = shopSelectors.data.staffs();
  const handlePickStaff = (id: string, data?: IStaffItemData) => {
    if (data)
      return onPickStaff(data);
    const staff = find(_allStaff, o => o.id === id);
    if (!staff) return;
    onPickStaff(staff);
  };

  const staffListStore = appointmentSelectors.getListStaffAvailableBooking();
  const [loading, setLoading] = useState(false);

  const availableStaffs = useMemo(() => {
    const _staffListStore = staffsProps || staffListStore;
    const data = _staffListStore || [];
    return data;
  }, [searchText, staffListStore, staffsProps]);

  const availableBySer = useMemo(() => staffsProps?.map(o => o.id) || [], [staffsProps]);


  const allStaff = useMemo(() => {
    if (!staffsProps) return _allStaff;
    return [...staffsProps].concat(_allStaff.filter(o => !availableBySer.includes(o.id)));
  }, [_allStaff, staffsProps, availableBySer]);

  const [nextAvailableStaffs, setNextAvailableStaffs] = useState<IStaffNextAvailableItem[]>([]);
  const nextAvailable = useMemo(() => {
    if (date?.format('MM-DD-YYYY') !== moment().format('MM-DD-YYYY')) return [];
    const _searchStr = searchText.trim().toLocaleLowerCase();
    if (!_searchStr?.length) return nextAvailableStaffs;
    return nextAvailableStaffs.filter(o => o.staffName?.toLocaleLowerCase().includes(_searchStr));
  }, [nextAvailableStaffs, searchText, date]);

  const fetchNextAvailable = async () => {
    setLoading(true);
    try {
      const res: IResponseDataBody<IStaffNextAvailableItem[]> = await appointmentApis.listStaffNextAvailable();
      if (res.data.data) {
        setNextAvailableStaffs(res.data.data || []);
      } else {
        setNextAvailableStaffs([]);
      }
    } catch (error) {
      setNextAvailableStaffs([]);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    setLoading,
    fetchNextAvailable,
  }));

  const listData = useMemo(() => {
    switch (activeTab) {
      case 'view-all':
        return allStaff;
      case 'available':
        return availableStaffs;
      default:
        return [];
    }
  }, [allStaff, availableStaffs, activeTab]);

  const filteredData = useMemo(() => {
    const _searchStr = searchText.trim().toLocaleLowerCase();
    if (!_searchStr.length) return listData;
    return listData.filter(o => o.firstName?.toLocaleLowerCase().includes(_searchStr));
  }, [listData, searchText]);

  return (
    <Container>
      <Box className='header' display='flex' flexDirection='column' pt='4' pr='2' gap='2'>
        <InputSearch onSearch={setSearchText} />
        <BoxModeToggle>
          <button onClick={() => setActiveTab('view-all')} className={activeTab === 'view-all' ? 'active' : ''} type='button'><span>All Staff</span></button>
          <button onClick={() => setActiveTab('available')} className={activeTab === 'available' ? 'active' : ''} type='button'><span>Available Staff</span></button>
          <button onClick={() => setActiveTab('next-available')} className={activeTab === 'next-available' ? 'active' : ''} type='button'><span>Next Staff Available</span></button>
        </BoxModeToggle>
      </Box>
      <div className="scroll-wrapper">
        <div className={'scroll-container'} >
          {activeTab !== 'next-available' && (<>
            {!filteredData.length && <EmptyBox>
              <span>Not Available</span>
            </EmptyBox>}
            <div className={'staff-list-container'}>
              {filteredData?.map((item, index) => {
                const selected = !!selectedStaffs.includes(item.id);
                const available = availableBySer.includes(item.id);
                return (
                  <StaffBox
                    key={item.id}
                    anybody={item.anyBody}
                    onClick={() => handlePickStaff(item.id, item)}
                    src={item.urlImage}
                    name={item.firstName}
                    selected={selected}
                    available={staffsProps ? available : undefined}
                    SelectedIconRender={SelectedIconRender}
                    className={`cashier-staff-box row-4 ${item.anyBody ? `anybody-staff anybody-staff_${index + 1}` : ''}`}
                  />
                );
              })}
            </div>
          </>)}

          {activeTab === 'next-available' && <>
            {!nextAvailable.length && <EmptyBox>
              <span>Not Available</span>
            </EmptyBox>}
            <div className={'staff-list-container'}>
              {nextAvailable?.map((item, index) => {
                const selected = !!selectedStaffs.includes(item.id);
                item.status === 'AVAILABLE';
                return (
                  <StaffBox
                    key={item.id}
                    onClick={() => handlePickStaff(item.id)}
                    src={item.avatar}
                    name={item.staffName}
                    selected={selected}
                    mode={item.status}
                    SelectedIconRender={SelectedIconRender}
                    className={'cashier-staff-box row-4'}
                    billNum={item.ticketNumber}
                    minutes={item.minutes}
                  />
                );
              })}
            </div>
          </>}
        </div>
        {loading && <div className={'scroll-container'} style={{ zIndex: 2 }}>
          <Spin spinning />
        </div>}
      </div>
    </Container>
  );
});
StaffListChooseUI.displayName = 'StaffListChooseUI';
export { StaffListChooseUI };
const EmptyBox = styled.div`
  position: absolute;
  inset: 0;
  z-index: 22;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1 0 0;
  align-self: stretch;

  span {
    color: #64748B;
    text-align: center;
    font-family: Poppins;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

const BoxModeToggle = styled.div`
display: flex;
padding: 4px;
align-items: flex-start;
border-radius: 8px;
background: #E5E6EB;
align-self: stretch;
flex: 0.8;
button {
  display: flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 2px;
  flex: 1 0 0;
  align-self: stretch;
  span {
    color: #1D2129;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }

  &.active {
    border-radius: 8px;
    background: #FFF;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.15);
    span {
      color: #2D6772;
      font-weight: 500;
    }
  }
}
`;

const SelectedIconRender = () => {
  return <SelectedIcon>
    <img src={selected_box_icon} alt="selected_box_icon" style={{ width: 48, height: 48 }} />
  </SelectedIcon>;
};
const SelectedIcon = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
`;
type InputSearchProps = {
  onSearch: (value: string) => void;
};
const InputSearch = ({ onSearch }: InputSearchProps) => {
  const [text, setText] = useState('');
  const _debounce = useCallback(debounce((_text: string) => onSearch(_text.trim()), 200), []);
  const onChange = (e: any) => {
    const searchText = e.target.value.trim();
    setText(searchText);
    _debounce(searchText);
  };
  return (
    <WrapperInputSearch>
      <Input className='search-input' placeholder="Search by Staff's Name" value={text} onChange={onChange}
        suffix={<SearchIcon fill={'#fff'} />}
      />
    </WrapperInputSearch>
  );
};

const WrapperInputSearch = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding-left: 16px;
  .search-input.ant-input-affix-wrapper {
    border-radius: 100px;
    padding: 0px;
    .ant-input-suffix {
      display: flex;
      width: 56px;
      height: 56px;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 0px 100px 100px 0px;
      background: #2D6772;
    }
    input {
      border-radius: 100px; 
      padding: 24px;
      height: 56px;
      font-family: Poppins;
      font-size: 22px;
      font-style: normal;
      font-weight: 600;
      line-height: 30px; /* 166.667% */
      text-transform: capitalize;
      align-self: center;
    }
    border: 3px solid #2D6772;
    background: #FFF;
  }
`;
const Container = styled.div`
    flex: 1;
    background-color: #F5F5F5;
    align-self: stretch;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    min-height: 0;
    .header {
      flex: 0 0 auto;
    }
    .group-zoom {
      display: flex;
      align-self: stretch;
      gap: 16px;
      justify-content: flex-end;
      padding: 8px 16px;
      padding-right: 0;
    }
    .scroll-wrapper {
      flex: 1;
      min-height: 0;
      position: relative;
      .scroll-container {
        position: absolute;
        inset: 0;
        overflow: auto;
        scrollbar-width: none; /* Firefox 64 */
        &::-webkit-scrollbar {
          width: 0px;
          height: 8px;
          background-color: #F5F5F5;
        }
      }
    }
    .staff-list-container {
      padding: 24px 16px;
      padding-top: 16px;
      display: grid;
      gap: 16px;
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
      .cashier-staff-box {
        width: 100%;
        padding: 0.5rem 0;
        justify-content: center;
        box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.08);
        .CONTENT_2 {
          color: #1D2129;
          text-align: center;
          font-size: 16px;
          font-weight: 600;
          line-height: 30px;
          text-transform: uppercase;
        }
         .AnyBodyImage {
            width: 7rem !important;
            min-height: 7rem;
          }
        .circle-avatar {
          width: 6rem;
          min-height: 6rem;
        }
        .AnyBodyImage {
          width: 5rem !important;
          min-height: 5rem;
        }
      }

      &.anybody-staff-list .cashier-staff-box.anybody-staff {
        grid-column: span 2;
        grid-row: span 2;
      }

      &.anybody-staff-list-container_1 .cashier-staff-box {
        &.anybody-staff .AnyBodyImage {
          zoom: 1.5;
        }  
      }
      &.anybody-staff-list-container_2 .cashier-staff-box {
        &.anybody-staff {
          min-height: 250px;
          .AnyBodyImage {
            zoom: 1.3;
          }            
        }  
      }
      &.anybody-staff-list-container_3 .cashier-staff-box {
        &.anybody-staff {
          grid-column: span 2;
          grid-row: unset !important;
        }  
        &.anybody-staff:nth-child(1) {
          .AnyBodyImage {
            zoom: 1.5;
          }            
        }  
      }
    
      &.anybody-staff-list-container_5 .cashier-staff-box {
        &.anybody-staff {
          grid-column: span 2;
          grid-row: span 2;
        }
        &.anybody-staff:nth-child(5) {
          .AnyBodyImage {
            zoom: 1.5;
          }            
        } 
      }
    }
`;
